import { useEffect, useRef, useState, useCallback } from "react";

const IDLE_MS = 5 * 60 * 1000;
const WARNING_SECONDS = 30;

export function useIdleTimer(onLogout) {
  const [warning, setWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_SECONDS);

  const idleTimer = useRef(null);
  const countdownRef = useRef(null);
  const warningRef = useRef(false);
  const lastResetRef = useRef(0);
  const isMountedRef = useRef(true);
  const onLogoutRef = useRef(onLogout);

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const clearSensitiveData = useCallback(() => {
    localStorage.removeItem("api_key");
    sessionStorage.removeItem("api_key");
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    localStorage.removeItem("last_activity");
    localStorage.removeItem("tab_closed_at");
    sessionStorage.removeItem("tab_closed_at");
    sessionStorage.clear();
  }, []);

  const saveTabCloseTimestamp = useCallback(() => {
    const now = Date.now();
    localStorage.setItem("tab_closed_at", now.toString());
    sessionStorage.setItem("tab_closed_at", now.toString());
  }, []);

  const checkAndHandleTabReopen = useCallback(() => {
    const closedAt = localStorage.getItem("tab_closed_at") || sessionStorage.getItem("tab_closed_at");

    if (closedAt) {
      const closedTime = parseInt(closedAt);
      const timeDifference = Date.now() - closedTime;

      if (timeDifference >= IDLE_MS) {
        clearSensitiveData();
        localStorage.removeItem("tab_closed_at");
        sessionStorage.removeItem("tab_closed_at");

        if (onLogoutRef.current && isMountedRef.current) {
          onLogoutRef.current();
        }
        return true;
      }

      localStorage.removeItem("tab_closed_at");
      sessionStorage.removeItem("tab_closed_at");
    }

    return false;
  }, [clearSensitiveData]);

  const clearAll = useCallback(() => {
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const startCountdown = useCallback(() => {
    if (!isMountedRef.current) return;
    if (warningRef.current) return;

    warningRef.current = true;
    setWarning(true);
    setCountdown(WARNING_SECONDS);

    let secs = WARNING_SECONDS;

    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    countdownRef.current = setInterval(() => {
      secs -= 1;

      if (isMountedRef.current) {
        setCountdown(secs);
      }

      if (secs <= 0) {
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
        }

        warningRef.current = false;

        if (isMountedRef.current) {
          setWarning(false);
        }

        clearSensitiveData();
        localStorage.setItem("logout", Date.now().toString());

        if (onLogoutRef.current && isMountedRef.current) {
          onLogoutRef.current();
        }
      }
    }, 1000);
  }, [clearSensitiveData]);

  const resetIdle = useCallback(() => {
    const now = Date.now();
    if (now - lastResetRef.current < 1000) return;
    lastResetRef.current = now;

    clearAll();

    if (warningRef.current) {
      warningRef.current = false;
      if (isMountedRef.current) {
        setWarning(false);
        setCountdown(WARNING_SECONDS);
      }
    }

    idleTimer.current = setTimeout(() => {
      startCountdown();
    }, IDLE_MS);
  }, [clearAll, startCountdown]);

  const stayActive = useCallback(() => {
    clearAll();

    warningRef.current = false;
    if (isMountedRef.current) {
      setWarning(false);
      setCountdown(WARNING_SECONDS);
    }

    const now = Date.now();
    localStorage.setItem("last_activity", now.toString());
    sessionStorage.setItem("last_activity", now.toString());

    idleTimer.current = setTimeout(() => {
      startCountdown();
    }, IDLE_MS);
  }, [clearAll, startCountdown]);

  useEffect(() => {
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];

    const wasExpired = checkAndHandleTabReopen();

    if (!wasExpired && onLogoutRef.current) {
      resetIdle();
    }

    const resetHandler = () => {
      if (onLogoutRef.current) {
        resetIdle();
      }
    };

    events.forEach(e => window.addEventListener(e, resetHandler));

    return () => {
      clearAll();
      events.forEach(e => window.removeEventListener(e, resetHandler));
    };
  }, [resetIdle, clearAll, checkAndHandleTabReopen]);

  useEffect(() => {
    const syncLogout = (e) => {
      if (e.key === "logout") {
        clearSensitiveData();
        if (onLogoutRef.current && isMountedRef.current) {
          onLogoutRef.current();
        }
      }
    };

    window.addEventListener("storage", syncLogout);
    return () => window.removeEventListener("storage", syncLogout);
  }, [clearSensitiveData]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveTabCloseTimestamp();
        clearAll();
      } else {
        const wasExpired = checkAndHandleTabReopen();
        if (!wasExpired && onLogoutRef.current) {
          resetIdle();
        }
      }
    };

    const handleBeforeUnload = () => {
      saveTabCloseTimestamp();
      clearAll();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [saveTabCloseTimestamp, checkAndHandleTabReopen, clearAll, resetIdle]);

  return { warning, countdown, stayActive };
}